import * as THREE from 'three'
import type { VpVec3 } from '@viewer/lib/viewpoint'

/**
 * Coordenadas na fronteira do ficheiro `.bcfv`.
 *
 * O BCF está nas coordenadas do IFC: Z para cima, no referencial do
 * próprio ficheiro. A cena do viewer é Y para cima (o IFC entra rodado
 * -90 graus em X, ver `levelsAndGrids.ts` e `geoTransform.ts`) e, num
 * modelo georreferenciado, ainda leva por cima o placement do mapa.
 *
 * Por isso a conversão faz-se em dois passos, e SÓ aqui, ao escrever e
 * ao ler o `.bcfv`. Os viewpoints guardados no IndexedDB continuam em
 * coordenadas da cena, como sempre estiveram.
 *
 * 1. `referencial`: a matriz que leva do referencial do ficheiro (já
 *    com Y para cima) para a cena. É `model.object.matrixWorld` com o
 *    recentramento do Fragments desfeito (ver `referencialDoModelo`).
 *    Num modelo sem georreferenciação é a identidade.
 * 2. Troca de eixos three para IFC: (x, y, z) passa a (x, -z, y), a
 *    mesma convenção de E/N/Cota do HUD de coordenadas e do KMZ.
 */

/** Matriz 4x4 em coluna (a ordem de `THREE.Matrix4.elements`). Fica em
 *  números simples para poder viajar sem depender da cena. */
export type Referencial = readonly number[]

/** Three (Y para cima) para IFC (Z para cima). */
export function threeParaIfc(v: VpVec3): VpVec3 {
  return { x: v.x, y: -v.z, z: v.y }
}

/** IFC (Z para cima) para three (Y para cima). Inversa de `threeParaIfc`. */
export function ifcParaThree(v: VpVec3): VpVec3 {
  return { x: v.x, y: v.z, z: -v.y }
}

function matriz(ref: Referencial | null | undefined): THREE.Matrix4 | null {
  if (!ref || ref.length !== 16) return null
  return new THREE.Matrix4().fromArray(ref as number[])
}

function vec(v: VpVec3): THREE.Vector3 {
  return new THREE.Vector3(v.x, v.y, v.z)
}

function simples(v: THREE.Vector3): VpVec3 {
  // Tira o -0 que as trocas de sinal deixam, para o XML não escrever "-0".
  const z = (n: number) => (Object.is(n, -0) ? 0 : n)
  return { x: z(v.x), y: z(v.y), z: z(v.z) }
}

/** Um ponto da cena para coordenadas IFC do ficheiro. */
export function pontoParaIfc(p: VpVec3, ref?: Referencial | null): VpVec3 {
  const m = matriz(ref)
  const local = m ? vec(p).applyMatrix4(m.clone().invert()) : vec(p)
  return threeParaIfc(simples(local))
}

/** Uma direcção da cena para IFC (só a parte linear, normalizada). */
export function direccaoParaIfc(d: VpVec3, ref?: Referencial | null): VpVec3 {
  const m = matriz(ref)
  const v = vec(d)
  if (m) v.transformDirection(m.clone().invert())
  else v.normalize()
  return threeParaIfc(simples(v))
}

/** Um ponto IFC do ficheiro para a cena. */
export function pontoDeIfc(p: VpVec3, ref?: Referencial | null): VpVec3 {
  const v = vec(ifcParaThree(p))
  const m = matriz(ref)
  if (m) v.applyMatrix4(m)
  return simples(v)
}

/** Uma direcção IFC para a cena (normalizada). */
export function direccaoDeIfc(d: VpVec3, ref?: Referencial | null): VpVec3 {
  const v = vec(ifcParaThree(d))
  const m = matriz(ref)
  if (m) v.transformDirection(m)
  else v.normalize()
  return simples(v)
}

/** O referencial de um modelo carregado: `matrixWorld · T(t)`, com `t`
 *  a translação que o Fragments aplicou ao recentrar a geometria. Com
 *  isto, cena = referencial · troca(IFC). Para um modelo sem
 *  georreferenciação o resultado é a identidade. */
export function referencialDoModelo(
  matrixWorld: THREE.Matrix4,
  recentre: readonly [number, number, number],
): Referencial {
  const m = matrixWorld
    .clone()
    .multiply(new THREE.Matrix4().makeTranslation(recentre[0], recentre[1], recentre[2]))
  return m.toArray()
}
